import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../config/supabase';
import { useNavigate } from 'react-router-dom';
import { useOpenAI } from '../contexts/OpenAIContext';
import { useGemini } from '../contexts/GeminiContext';
import { useParameterBuilder } from '../contexts/ParameterBuilder';

const ReadyPrompt = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [keywords, setKeywords] = useState('');
  const [profile, setProfile] = useState(null);
  const [model, setModel] = useState('Polar Bear');
  const [amount, setAmount] = useState('1');
  const [generatedPrompt, setGeneratedPrompt] = useState('');
  const [fullPrompts, setFullPrompts] = useState([]); // เก็บ prompts ทั้งหมดแยกบรรทัด
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [rawPrompts, setRawPrompts] = useState([]); // เก็บ prompts ที่ได้จาก AI โดยตรง
  const [totalCost, setTotalCost] = useState(null);
  // ตั้งค่าเริ่มต้นของ aiProvider โดยอ่านค่าจาก localStorage ตั้งแต่แรก
  const initialAiProvider = localStorage.getItem('aiProvider') || 'gemini';
  console.log('Initial AI Provider:', initialAiProvider);
  const [aiProvider, setAiProvider] = useState(initialAiProvider); // ใช้ค่าจาก localStorage หรือค่าเริ่มต้น 'gemini'

  const { generatePrompt: generateOpenAIPrompt } = useOpenAI();
  const { generatePrompt: generateGeminiPrompt, generatePromptFree: generateGeminiPromptFree, isRateLimited } = useGemini();
  const { buildFinalPrompts } = useParameterBuilder();

  const [styles, setStyles] = useState({
    realistic: true,
    illustrations: false,
    watercolor: false,
    anime: false,
    digitalArt: false,
    oilPainting: false
  });

  const [aspectRatios, setAspectRatios] = useState({
    '1:1': true,
    '16:9': false,
    '9:16': true,
    '4:3': false,
    '3:4': false,
    '3:2': false,
    '2:1': false,
    '1:2': false
  });

  const [stylize, setStylize] = useState({
    '0': false,
    '50': false,
    '100': true,
    '200': false,
    '250': false,
    '350': false,
    '500': false,
    '750': false
  });

  const [chaos, setChaos] = useState({
    '0': false,
    '10': false,
    '20': false,
    '30': false,
    '50': false,
    '70': false,
    '80': false,
    '90': false
  });

  const [quality, setQuality] = useState('none');
  const [version, setVersion] = useState('7');
  const [copySpace, setCopySpace] = useState(false);
  const [whiteBackground, setWhiteBackground] = useState(false);

  const [additionalParams, setAdditionalParams] = useState('');
  const [no, setNo] = useState('');
  const [personalization, setPersonalization] = useState('');
  const [prefix, setPrefix] = useState('');
  const [suffix, setSuffix] = useState('');

  const [customAspectRatios, setCustomAspectRatios] = useState([]);
  const [customAspectRatioInput, setCustomAspectRatioInput] = useState('');

  // Helper function สำหรับอัพเดท checkbox state
  const updateCheckboxState = (stateSetter, stateObj, key) => {
    stateSetter(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Helper function สำหรับสร้าง parameters object
  const createParametersObject = useCallback(() => {
    return {
      prefix,
      suffix,
      additionalParams,
      no,
      personalization,
      styles: Object.entries(styles)
        .filter(([_, value]) => value)
        .map(([key]) => key),
      aspectRatios: [
        ...Object.entries(aspectRatios)
          .filter(([_, value]) => value)
          .map(([key]) => key),
        ...customAspectRatios
      ],
      stylize: Object.entries(stylize)
        .filter(([_, value]) => value)
        .map(([key]) => key),
      chaos: Object.entries(chaos)
        .filter(([_, value]) => value)
        .map(([key]) => key),
      quality,
      version,
      copySpace,
      whiteBackground
    };
  }, [prefix, suffix, additionalParams, no, personalization, styles, aspectRatios, 
      customAspectRatios, stylize, chaos, quality, version, copySpace, whiteBackground]);

  // Helper function สำหรับโหลดการตั้งค่าจาก localStorage
  const loadSettingsFromStorage = (settings) => {
    try {
      // ตั้งค่า styles
      if (settings.styles) {
        const newStyles = { ...styles };
        Object.keys(newStyles).forEach(key => {
          newStyles[key] = settings.styles.includes(key);
        });
        setStyles(newStyles);
      }

      // ตั้งค่า aspect ratios
      if (settings.aspectRatios) {
        const newAspectRatios = { ...aspectRatios };
        Object.keys(newAspectRatios).forEach(key => {
          newAspectRatios[key] = settings.aspectRatios.includes(key);
        });
        setAspectRatios(newAspectRatios);
        setCustomAspectRatios(settings.customAspectRatios || []);
      }

      // ตั้งค่า stylize
      if (settings.stylize) {
        const newStylize = { ...stylize };
        Object.keys(newStylize).forEach(key => {
          newStylize[key] = settings.stylize.includes(key);
        });
        setStylize(newStylize);
      }

      // ตั้งค่า chaos
      if (settings.chaos) {
        const newChaos = { ...chaos };
        Object.keys(newChaos).forEach(key => {
          newChaos[key] = settings.chaos.includes(key);
        });
        setChaos(newChaos);
      }

      // ตั้งค่าอื่นๆ
      if (settings.quality) setQuality(settings.quality);
      if (settings.version) setVersion(settings.version);
      if (settings.prefix) setPrefix(settings.prefix);
      if (settings.suffix) setSuffix(settings.suffix);
      if (settings.additionalParams) setAdditionalParams(settings.additionalParams);
      if (settings.no) setNo(settings.no);
      if (settings.personalization) setPersonalization(settings.personalization);
      // ตั้งค่า AI Provider
      if (settings.aiProvider) setAiProvider(settings.aiProvider);
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  // โหลดค่า aiProvider จาก localStorage เมื่อโหลดหน้าเว็บ
  useEffect(() => {
    // ตรวจสอบค่าใน localStorage ทั้งหมด
    console.log('All localStorage items:');
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      console.log(`${key}: ${localStorage.getItem(key)}`);
    }
    
    const savedAiProvider = localStorage.getItem('aiProvider');
    console.log('Directly checking aiProvider in localStorage:', savedAiProvider);
    
    if (savedAiProvider) {
      console.log('Setting AI Provider to:', savedAiProvider);
      setAiProvider(savedAiProvider);
    } else {
      console.log('No AI Provider found in localStorage, using default:', aiProvider);
    }
  }, []);
  
  // บันทึกค่า aiProvider ลง localStorage ทุกครั้งที่มีการเปลี่ยนแปลง
  useEffect(() => {
    console.log('aiProvider changed to:', aiProvider);
    localStorage.setItem('aiProvider', aiProvider);
  }, [aiProvider]);

  // ตรวจสอบสิทธิ์และโหลดการตั้งค่า
  useEffect(() => {
    const checkAuth = async () => {
      if (!user) {
        navigate('/');
        return;
      }

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();

        if (error || !data) {
          navigate('/');
          return;
        }

        // เพิ่มการตรวจสอบ membership
        const allowedMemberships = ['admin', 'vip', 'lifetime'];
        if (!allowedMemberships.includes(data.membership?.toLowerCase())) {
          navigate('/dashboard');
          return;
        }

        setProfile(data);

        // โหลดการตั้งค่าจาก localStorage เท่านั้น
        const savedAiProvider = localStorage.getItem('aiProvider');
        if (savedAiProvider) {
          setAiProvider(savedAiProvider);
        }
        
        // โหลดการตั้งค่าจาก localStorage ถ้าไม่มีข้อมูลในฐานข้อมูล
        const savedSettings = localStorage.getItem('promptSettings');
        if (savedSettings) {
          const parsedSettings = JSON.parse(savedSettings);
          if (parsedSettings) {
            loadSettingsFromStorage(parsedSettings);
          }
        }
      } catch (error) {
        navigate('/');
      }
    };

    checkAuth();
  }, [user, navigate]);

  const handleKeywordsChange = (event) => {
    setKeywords(event.target.value);
  };

  const handleAIPrompt = async () => {
    try {
      setLoading(true);
      setError('');
      setTotalCost(null);
      
      const keywordList = keywords.split('\n').filter(k => k.trim());
      if (keywordList.length === 0) {
        throw new Error('กรุณาใส่คีย์เวิร์ดอย่างน้อย 1 คำ');
      }

      // คำนวณจำนวน prompts ทั้งหมดที่จะต้องสร้าง
      const totalPrompts = keywordList.length * parseInt(amount);
      setProgress({ current: 0, total: totalPrompts });

      // สร้าง prompts สำหรับทุก keyword
      const allPrompts = [];
      const rawKeywordsList = [];
      let totalCostData = {
        prompt_tokens: 0,
        completion_tokens: 0,
        total_tokens: 0,
        input_cost_usd: 0,
        output_cost_usd: 0,
        total_cost_usd: 0,
        total_cost_thb: 0
      };
      
      // เลือกฟังก์ชันที่จะใช้สร้าง prompt ตาม provider ที่เลือก
      console.log('Current AI Provider:', aiProvider);
      
      let generatePromptFunction;
      if (aiProvider === 'openai') {
        generatePromptFunction = generateOpenAIPrompt;
        console.log('Using function: OpenAI');
      } else if (aiProvider === 'geminiFree') {
        generatePromptFunction = generateGeminiPromptFree;
        console.log('Using function: Gemini Free');
      } else {
        generatePromptFunction = generateGeminiPrompt;
        console.log('Using function: Gemini');
      }
      
      try {
        for (const keyword of keywordList) {
          // ขอผลลัพธ์จาก AI provider ที่เลือก
          const result = await generatePromptFunction(model, keyword, amount);
          const promptArray = result.content.split('|').map(p => p.trim());
          
          // รวมค่าใช้จ่าย
          Object.keys(totalCostData).forEach(key => {
            if (result.cost[key] !== undefined) {
              totalCostData[key] += result.cost[key];
            }
          });
          
          // เก็บคีย์เวิร์ดดิบไว้ก่อน
          const rawKeywords = promptArray.map(prompt => {
            const parts = prompt.split('--');
            return parts[0].trim();
          });
          rawKeywordsList.push(...rawKeywords);
          
          // เก็บ prompts เต็มสำหรับสร้าง final prompts
          allPrompts.push(...promptArray);
          
          // อัพเดทความคืบหน้า
          setProgress(prev => ({ ...prev, current: prev.current + parseInt(amount) }));
        }
      } catch (apiError) {
        // จัดการข้อผิดพลาดจาก API โดยเฉพาะ
        if (apiError.message.includes('API key')) {
          setError(`${aiProvider === 'openai' ? 'OpenAI' : 'Gemini'} API key ไม่ถูกต้อง กรุณาตรวจสอบและแก้ไขใน<a href="/profile">หน้าโปรไฟล์</a>`);
          throw apiError;
        } else if (apiError.message.includes('Provider')) {
          setError(`กรุณาตั้งค่า ${aiProvider === 'openai' ? 'OpenAI' : 'Gemini'} API Key ใน<a href="/profile">หน้าโปรไฟล์</a>ก่อน`);
          throw apiError;
        } else {
          throw apiError;
        }
      }

      // เซ็ตค่าคีย์เวิร์ดดิบทันที
      setRawPrompts(rawKeywordsList);
      setTotalCost(totalCostData);

      // สร้าง parameter object และ final prompts
      const parameters = createParametersObject();
      const finalPrompts = buildFinalPrompts(allPrompts, parameters);
      setGeneratedPrompt(finalPrompts[0]);
      setFullPrompts(finalPrompts);

    } catch (err) {
      console.error('Error in handleAIPrompt:', err);
      // ถ้ายังไม่มีการตั้งค่า error จากด้านบน
      if (!error) {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePrompt = () => {
    // แยก keywords เป็นแต่ละบรรทัด
    const promptList = keywords.split('\n').filter(keyword => keyword.trim() !== '');
    if (promptList.length === 0) return;
    
    // สร้าง parameter object และ final prompts
    const parameters = createParametersObject();
    const finalPrompts = buildFinalPrompts(promptList, parameters);
    
    // สุ่มเลือก 1 prompt จากทั้งหมดเพื่อแสดงผล
    const randomIndex = Math.floor(Math.random() * finalPrompts.length);
    const selectedPrompt = finalPrompts[randomIndex];
    
    // แสดงผลเฉพาะ prompt ที่สุ่มได้ แต่เก็บ prompts ทั้งหมดไว้
    setGeneratedPrompt(selectedPrompt);
    setFullPrompts(finalPrompts); // เก็บ prompts ทั้งหมดไว้สำหรับคัดลอกและดาวน์โหลด
  };

  // Event handlers สำหรับ checkbox options
  const handleStyleChange = (style) => updateCheckboxState(setStyles, styles, style);
  const handleAspectRatioChange = (ratio) => updateCheckboxState(setAspectRatios, aspectRatios, ratio);
  const handleStylizeChange = (value) => updateCheckboxState(setStylize, stylize, value);
  const handleChaosChange = (value) => updateCheckboxState(setChaos, chaos, value);

  const handleQualityChange = (e) => {
    setQuality(e.target.value);
  };

  const handleVersionChange = (e) => {
    setVersion(e.target.value);
  };

  const handleCopySpaceChange = (e) => {
    setCopySpace(e.target.checked);
  };

  const handleWhiteBackgroundChange = (e) => {
    setWhiteBackground(e.target.checked);
  };

  const handleGeneratePrompt = () => {
    // TODO: Implement Prompt generation
    setGeneratedPrompt('aaa, realistic, photography --ar 1:1 --v 6.1 --q 1');
  };

  const handleCopyAll = () => {
    // คัดลอกทุก prompt แยกบรรทัด
    const allPrompts = fullPrompts.map(prompt => prompt.replace(/"/g, '')).join('\n');
    navigator.clipboard.writeText(allPrompts);
  };

  const handleDownloadTxt = () => {
    // ดาวน์โหลดทุก prompt แยกบรรทัด
    const allPrompts = fullPrompts.map(prompt => prompt.replace(/"/g, '')).join('\n');
    const element = document.createElement('a');
    const file = new Blob([allPrompts], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = 'prompts.txt';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const handleAdditionalParamsChange = (event) => {
    setAdditionalParams(event.target.value);
  };

  const handleNoChange = (event) => {
    setNo(event.target.value);
  };

  const handlePersonalizationChange = (event) => {
    setPersonalization(event.target.value);
  };

  const handlePrefixChange = (event) => {
    setPrefix(event.target.value);
  };

  const handleSuffixChange = (event) => {
    setSuffix(event.target.value);
  };



  const handleAddCustomAspectRatio = () => {
    if (customAspectRatioInput && !customAspectRatios.includes(customAspectRatioInput)) {
      setCustomAspectRatios([...customAspectRatios, customAspectRatioInput]);
      setCustomAspectRatioInput(''); // เคลียร์ input หลังจากเพิ่มแล้ว
    }
  };

  const handleClearCustomAspectRatios = () => {
    setCustomAspectRatios([]);
    setCustomAspectRatioInput('');
  };

  const handleSaveSettings = async () => {
    try {
      // สร้าง parameters object แต่ปรับให้เป็นรูปแบบที่เหมาะสมสำหรับการบันทึก
      const params = createParametersObject();
      
      // ตรวจสอบค่า aiProvider ปัจจุบันก่อนบันทึก
      console.log('Current aiProvider before saving:', aiProvider);
      
      const settingsToSave = {
        ...params,
        // เพิ่ม customAspectRatios แยกต่างหาก เพราะไม่ได้อยู่ใน parameters object ปกติ
        customAspectRatios,
        // เพิ่มการบันทึกค่า AI Provider
        aiProvider: aiProvider
      };
      
      // บันทึกลง localStorage เท่านั้น
      localStorage.setItem('promptSettings', JSON.stringify(settingsToSave));
      localStorage.setItem('aiProvider', aiProvider);
      console.log('Saved aiProvider to localStorage:', aiProvider);
      
      alert('บันทึกการตั้งค่าเรียบร้อยแล้ว');
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('เกิดข้อผิดพลาดในการบันทึกการตั้งค่า');
    }
  };

  const handleCopyRawPrompts = () => {
    if (rawPrompts.length > 0) {
      const text = rawPrompts.join('\n');
      navigator.clipboard.writeText(text);
    }
  };

  // แสดง loading ระหว่างตรวจสอบสิทธิ์
  if (!profile) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-purple-500 mx-auto mb-4"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      {/* ส่วนแนะนำการใช้งาน AI Provider ต่างๆ */}
      <div className="max-w-4xl mx-auto px-4 py-6 bg-gray-800 rounded-lg shadow-xl mb-6">
        <h2 className="text-xl font-bold text-gray-100 mb-4 border-b border-gray-700 pb-2">
          คำแนะนำการใช้งาน AI Provider
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          {/* OpenAI */}
          <div className="bg-gray-900 p-4 rounded-lg border border-gray-700 hover:border-purple-500 transition-all">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-gray-100">OpenAI</h3>
              <span className="px-2 py-1 bg-green-900 text-green-300 text-xs rounded-full">แนะนำ</span>
            </div>
            <p className="text-gray-300 text-sm mb-3">
              บริการ AI ที่มีประสิทธิภาพสูง รองรับหลายโมเดล เช่น GPT-4o-mini มีความสามารถในการสร้าง prompt ที่มีคุณภาพสูง
            </p>
            <div className="space-y-2 text-sm text-gray-400 mb-4">
              <div className="flex items-start">
                <span className="mr-2">•</span>
                <span>ราคา: $0.00015 / 1K input tokens, $0.0006 / 1K output tokens</span>
              </div>
              <div className="flex items-start">
                <span className="mr-2">•</span>
                <span>ต้องใช้บัตรเครดิตในการสมัคร</span>
              </div>
            </div>
            <a 
              href="https://platform.openai.com/signup" 
              target="_blank" 
              rel="noopener noreferrer"
              className="block w-full text-center bg-green-700 hover:bg-green-600 text-white py-2 rounded-lg transition-colors"
            >
              สมัครใช้งาน OpenAI
            </a>
          </div>
          
          {/* Gemini */}
          <div className="bg-gray-900 p-4 rounded-lg border border-gray-700 hover:border-purple-500 transition-all">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-gray-100">Gemini</h3>
              <span className="px-2 py-1 bg-blue-900 text-blue-300 text-xs rounded-full">ยอดนิยม</span>
            </div>
            <p className="text-gray-300 text-sm mb-3">
              บริการ AI จาก Google ที่มีประสิทธิภาพสูง ให้ผลลัพธ์ที่มีคุณภาพดีและมีความเสถียร เหมาะสำหรับการใช้งานทั่วไป
            </p>
            <div className="space-y-2 text-sm text-gray-400 mb-4">
              <div className="flex items-start">
                <span className="mr-2">•</span>
                <span>ราคา: $0.00035 / 1K input tokens, $0.00105 / 1K output tokens</span>
              </div>
              <div className="flex items-start">
                <span className="mr-2">•</span>
                <span>ไม่ต้องใช้บัตรเครดิตในการสมัคร</span>
              </div>
            </div>
            <a 
              href="https://aistudio.google.com/app/apikey" 
              target="_blank" 
              rel="noopener noreferrer"
              className="block w-full text-center bg-blue-700 hover:bg-blue-600 text-white py-2 rounded-lg transition-colors"
            >
              สมัครใช้งาน Gemini
            </a>
          </div>
          
          {/* Gemini Free */}
          <div className="bg-gray-900 p-4 rounded-lg border border-gray-700 hover:border-purple-500 transition-all">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-gray-100">Gemini Free</h3>
              <span className="px-2 py-1 bg-purple-900 text-purple-300 text-xs rounded-full">ฟรี</span>
            </div>
            <p className="text-gray-300 text-sm mb-3">
              ใช้งาน Gemini API แบบฟรี มีการจำกัดอัตราการส่งคำขอที่ 15 ครั้งต่อนาที และประมาณ 1500 ต่อวัน
            </p>
            <div className="space-y-2 text-sm text-gray-400 mb-4">
              <div className="flex items-start">
                <span className="mr-2">•</span>
                <span>ฟรี: จำกัด 15 คำขอต่อนาที และประมาณ 1500 ต่อวัน</span>
              </div>
              <div className="flex items-start">
                <span className="mr-2">•</span>
                <span>ใช้ API key เดียวกับ Gemini</span>
              </div>
              
            </div>
            <a 
              href="https://aistudio.google.com/app/apikey" 
              target="_blank" 
              rel="noopener noreferrer"
              className="block w-full text-center bg-purple-700 hover:bg-purple-600 text-white py-2 rounded-lg transition-colors"
            >
              สมัครใช้งาน Gemini
            </a>
          </div>
        </div>
        
        <div className="bg-gray-900 p-4 rounded-lg border border-gray-700">
          <h3 className="text-lg font-semibold text-gray-100 mb-2">คำแนะนำในการใช้งาน</h3>
          <ul className="list-disc pl-5 text-gray-300 text-sm space-y-2">
            <li>คุณต้องมี API key ของ Provider ที่ต้องการใช้งานก่อน โดยสามารถสมัครได้ที่ลิงค์ด้านบน</li>
            <li>เพิ่ม API key ในหน้า <a href="/profile" className="text-purple-400 hover:underline">โปรไฟล์</a> เพื่อเริ่มใช้งาน</li>
            <li>OpenAI ให้ผลลัพธ์ที่มีคุณภาพสูงแต่มีค่าใช้จ่ายมากกว่า Gemini</li>
            <li>Gemini Free เหมาะสำหรับการทดลองใช้งานหรือผู้ที่มีงบประมาณจำกัด แต่มีข้อจำกัดในการใช้งาน</li>
            <li>ถ้าต้องการใช้ AI ให้กดปุ่มสีม่วงสำหรับสร้าง Prompt ถ้าสร้างแบบธรรมดา กดปุ่มสีฟ้า</li>
          </ul>
        </div>
      </div>
      
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {/* Ready Prompt Section */}
        <div className="bg-gray-800 rounded-lg shadow-xl p-6">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-gray-100">
              Ready Prompt ช่วยสร้าง Promptด้วย AI
            </h1>
            {totalCost && (
              <div className="text-right text-sm text-gray-400">
                <p>จำนวน Token: {totalCost.total_tokens.toLocaleString()} ({totalCost.prompt_tokens.toLocaleString()} input, {totalCost.completion_tokens.toLocaleString()} output)</p>
                <p>ค่าใช้จ่าย: ${totalCost.total_cost_usd.toFixed(4)} (฿{totalCost.total_cost_thb.toFixed(2)})</p>
              </div>
            )}
          </div>
          
          {/* ส่วนแสดงผลลัพธ์ */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-lg text-gray-300">
                ตัวอย่างผลลัพธ์ (จากทั้งหมด {fullPrompts.length} prompts)
              </h2>
              <div className="flex gap-2">
                <button
                  onClick={handleCopyRawPrompts}
                  className={`flex items-center gap-1 px-4 py-1.5 rounded-lg transition-colors text-sm ${
                    rawPrompts.length 
                    ? 'bg-gray-600 hover:bg-gray-700 text-white cursor-pointer' 
                    : 'bg-gray-400 text-gray-300 cursor-not-allowed'
                  }`}
                  disabled={!rawPrompts.length}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                  </svg>
                  คัดลอกคีย์เวิร์ด
                </button>
                <button
                  onClick={handleCopyAll}
                  className="flex items-center gap-1 bg-purple-600 text-white px-4 py-1.5 rounded-lg hover:bg-purple-700 transition-colors text-sm"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                  </svg>
                  คัดลอกทั้งหมด
                </button>
                <button
                  onClick={handleDownloadTxt}
                  className="flex items-center gap-1 bg-green-600 text-white px-4 py-1.5 rounded-lg hover:bg-green-700 transition-colors text-sm"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  ดาวน์โหลด .txt
                </button>
              </div>
            </div>
            <div className="bg-gray-900 p-4 rounded-lg font-mono text-sm text-gray-300 flex justify-between items-center min-h-[40px]">
              <div>{generatedPrompt || 'ยังไม่มีผลลัพธ์'}</div>
            </div>
            
            {/* แสดงข้อผิดพลาดหลังจากกดปุ่ม AI Prompt */}
            {error && (
              <div className="mt-4 p-4 bg-red-500/20 border border-red-500 rounded-lg text-red-300">
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span dangerouslySetInnerHTML={{ __html: error }}></span>
                </div>
              </div>
            )}
          </div>
          
          {/* ส่วนกรอกคีย์เวิร์ด */}
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">
              กรุณากรอก keywords (1 คีย์เวิร์ดต่อ 1 บรรทัด):
            </label>
            <textarea
              rows="5"
              className="w-full p-4 bg-gray-900 text-gray-100 border border-gray-700 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none font-mono text-sm"
              value={keywords}
              onChange={handleKeywordsChange}
              placeholder="พิมพ์ keywords ที่นี่&#10;1 คีย์เวิร์ดต่อ 1 บรรทัด"
            />
          </div>
          
          <div className="flex items-end gap-4">
            <div className="text-center">
              <label className="block text-gray-300 text-sm font-medium mb-1">
                AI Provider
              </label>
              <select
                value={aiProvider}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setAiProvider(newValue);
                  // บันทึกค่าลง localStorage ทันที
                  localStorage.setItem('aiProvider', newValue);
                  console.log('AI Provider changed to:', newValue);
                }}
                className="bg-gray-900 text-gray-100 border border-gray-700 rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent min-w-[120px]"
              >
                <option value="openai">OpenAI</option>
                <option value="gemini">Gemini</option>
                <option value="geminiFree">Gemini Free</option>
              </select>
            </div>
            
            <div className="text-center">
              <label className="block text-gray-300 text-sm font-medium mb-1">
                เลือกโมเดล
              </label>
              <select
                value={model}
                onChange={(e) => setModel(e.target.value)}
                className="bg-gray-900 text-gray-100 border border-gray-700 rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent min-w-[100px]"
              >
                <option value="Polar Bear">Polar Bear</option>
                <option value="Chicken">Chicken</option>
              </select>
            </div>

            <div className="text-center">
              <label className="block text-gray-300 text-sm font-medium mb-1">
                จำนวน Prompt
              </label>
              <select
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="bg-gray-900 text-gray-100 border border-gray-700 rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent min-w-[80px]"
              >
                {[1, 3, 5, 10, 15, 20, 30, 50].map(num => (
                  <option key={num} value={num}>{num}</option>
                ))}
              </select>
            </div>

            <div className="flex gap-8">
              <div className="text-center">
                <label className="block text-gray-300 text-sm font-medium mb-1">
                  สร้างด้วย {aiProvider === 'openai' ? 'OpenAI' : 'Gemini'} (มีค่าใช้จ่าย)
                </label>
                <button
                  onClick={handleAIPrompt}
                  disabled={loading}
                  className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-gray-900 min-w-[120px]"
                >
                  {loading ? 'กำลังสร้าง...' : 'AI Prompt'}
                </button>
              </div>

              <div className="text-center">
                <label className="block text-gray-300 text-sm font-medium mb-1">
                  สร้างธรรมดา
                </label>
                <button
                  onClick={handleCreatePrompt}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-900 min-w-[120px]"
                >
                  สร้าง Prompt
                </button>
              </div>
            </div>

            {/* แสดงจำนวนคีย์เวิร์ดและ prompts */}
            <div className="mt-4 flex gap-4 text-sm text-gray-400">
              <div>
                จำนวนคีย์เวิร์ด: <span className="text-green-400">{keywords.split('\n').filter(k => k.trim()).length}</span>
              </div>
              <div>
                จำนวน Prompt ที่จะได้: <span className="text-green-400">{keywords.split('\n').filter(k => k.trim()).length * parseInt(amount)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Parameter Section */}
        <div className="bg-gray-800 rounded-lg shadow-xl p-6">
          <h2 className="text-2xl font-bold text-gray-100 mb-6">Parameter</h2>
          
          {/* Style */}
          <div className="mb-4">
            <label className="block text-gray-300 text-sm font-medium mb-2">Style</label>
            <div className="flex flex-wrap gap-3">
              {['Realistic', 'Illustrations', 'Watercolor', 'Anime', 'Digital Art', 'Oil Painting'].map((style) => (
                <label key={style} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={styles[style.toLowerCase()]}
                    onChange={() => handleStyleChange(style.toLowerCase())}
                    className="form-checkbox text-purple-500 rounded border-gray-600 bg-gray-800 focus:ring-purple-500"
                  />
                  <span className="text-gray-300 text-sm">{style}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Aspect Ratio */}
          <div className="mb-6">
            <h2 className="text-xl text-gray-300 font-semibold mb-4">Aspect Ratio</h2>
            <div className="flex flex-wrap gap-4 mb-4">
              {['1:1', '16:9', '9:16', '4:3', '3:4', '3:2', '2:1', '1:2'].map((ratio) => (
                <label key={ratio} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={aspectRatios[ratio]}
                    onChange={() => handleAspectRatioChange(ratio)}
                    className="form-checkbox text-purple-500 rounded border-gray-600 bg-gray-800 focus:ring-purple-500"
                  />
                  <span className="text-gray-300 text-sm">{ratio}</span>
                </label>
              ))}
            </div>

            {/* ส่วนของ Custom Aspect Ratio */}
            <div className="flex items-center space-x-2 mb-4">
              <input
                type="text"
                value={customAspectRatioInput}
                onChange={(e) => setCustomAspectRatioInput(e.target.value)}
                placeholder="Custom AR: -"
                className="bg-gray-700 text-white px-3 py-2 rounded"
              />
              <button
                onClick={handleAddCustomAspectRatio}
                className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
              >
                Add
              </button>
              <button
                onClick={handleClearCustomAspectRatios}
                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
              >
                Clear
              </button>
            </div>

            {/* แสดง Custom Aspect Ratios ที่เพิ่มแล้ว */}
            {customAspectRatios.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {customAspectRatios.map((ratio, index) => (
                  <span key={index} className="px-2 py-1 bg-purple-500 text-white rounded">
                    {ratio}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Stylize */}
          <div className="mb-4">
            <label className="block text-gray-300 text-sm font-medium mb-2">Stylize</label>
            <div className="flex flex-wrap gap-3">
              {['0', '50', '100', '200', '250', '350', '500', '750'].map((value) => (
                <label key={value} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={stylize[value]}
                    onChange={() => handleStylizeChange(value)}
                    className="form-checkbox text-purple-500 rounded border-gray-600 bg-gray-800 focus:ring-purple-500"
                  />
                  <span className="text-gray-300 text-sm">{value}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Chaos */}
          <div className="mb-4">
            <label className="block text-gray-300 text-sm font-medium mb-2">Chaos</label>
            <div className="flex flex-wrap gap-3">
              {['0', '10', '20', '30', '50', '70', '80', '90'].map((value) => (
                <label key={value} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={chaos[value]}
                    onChange={() => handleChaosChange(value)}
                    className="form-checkbox text-purple-500 rounded border-gray-600 bg-gray-800 focus:ring-purple-500"
                  />
                  <span className="text-gray-300 text-sm">{value}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Additional Settings */}
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">Additional Settings</label>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-4">
                <label className="block text-gray-300 text-sm font-medium">Quality:</label>
                <select
                  value={quality}
                  onChange={handleQualityChange}
                  className="px-3 py-1 bg-gray-800 text-gray-300 rounded border border-gray-600 focus:ring-purple-500 focus:border-purple-500"
                >
                  <option value="High">High</option>
                  <option value="Standard">Standard</option>
                  <option value="none">none</option>
                </select>
              </div>

              <div className="flex items-center gap-4">
                <label className="block text-gray-300 text-sm font-medium">Version:</label>
                <select
                  value={version}
                  onChange={handleVersionChange}
                  className="px-3 py-1 bg-gray-800 text-gray-300 rounded border border-gray-600 focus:ring-purple-500 focus:border-purple-500"
                >
                  <option value="5.2">5.2</option>
                  <option value="6.1">6.1</option>
                  <option value="7">7</option>
                  <option value="Niji">Niji</option>
                  <option value="none">none</option>
                </select>
              </div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={copySpace}
                  onChange={handleCopySpaceChange}
                  className="form-checkbox text-purple-500 rounded border-gray-600 bg-gray-800 focus:ring-purple-500"
                />
                <span className="text-gray-300 text-sm">Copy Space</span>
              </label>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={whiteBackground}
                  onChange={handleWhiteBackgroundChange}
                  className="form-checkbox text-purple-500 rounded border-gray-600 bg-gray-800 focus:ring-purple-500"
                />
                <span className="text-gray-300 text-sm">White Background</span>
              </label>
            </div>
          </div>

          {/* More Settings */}
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">More Settings</label>
            <div className="grid grid-cols-4 gap-4">
              <div>
                <label className="block text-gray-300 text-xs mb-1" htmlFor="prefix">Prefix</label>
                <input
                  type="text"
                  id="prefix"
                  value={prefix}
                  onChange={handlePrefixChange}
                  className="w-full px-3 py-1 bg-gray-800 border border-gray-600 rounded text-gray-300"
                />
              </div>
              <div>
                <label className="block text-gray-300 text-xs mb-1" htmlFor="suffix">Suffix</label>
                <input
                  type="text"
                  id="suffix"
                  value={suffix}
                  onChange={handleSuffixChange}
                  className="w-full px-3 py-1 bg-gray-800 border border-gray-600 rounded text-gray-300"
                />
              </div>
              <div>
                <label className="block text-gray-300 text-xs mb-1" htmlFor="additionalParams">Additional Parameters</label>
                <input
                  type="text"
                  id="additionalParams"
                  value={additionalParams}
                  onChange={handleAdditionalParamsChange}
                  className="w-full px-3 py-1 bg-gray-800 border border-gray-600 rounded text-gray-300"
                />
              </div>
              <div>
                <label className="block text-gray-300 text-xs mb-1" htmlFor="no">No</label>
                <input
                  type="text"
                  id="no"
                  value={no}
                  onChange={handleNoChange}
                  className="w-full px-3 py-1 bg-gray-800 border border-gray-600 rounded text-gray-300"
                />
              </div>
            </div>
            <div className="grid grid-cols-4 gap-4">
              <div>
                <label className="block text-gray-300 text-xs mb-1" htmlFor="personalization">Personalization</label>
                <textarea
                  id="personalization"
                  value={personalization}
                  onChange={handlePersonalizationChange}
                  className="w-full h-24 px-3 py-2 bg-gray-800 border border-gray-600 rounded text-gray-300 resize-none"
                />
              </div>
            </div>
          </div>
          <div className="flex justify-between items-center mb-4">
            <button
              onClick={handleSaveSettings}
              className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors"
            >
              บันทึกการตั้งค่า
            </button>
          </div>
        </div>
      </div>

      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-lg mb-2">กำลังประมวลผล...</p>
            {progress.total > 0 && (
              <div>
                <p className="text-sm text-gray-600">
                  สร้าง Prompt {progress.current} จาก {progress.total}
                </p>
                <div className="w-full bg-gray-200 rounded-full h-2.5 mt-2">
                  <div 
                    className="bg-blue-500 h-2.5 rounded-full transition-all duration-300" 
                    style={{ width: `${(progress.current / progress.total) * 100}%` }}
                  ></div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      

    </div>
  );
};

export default ReadyPrompt;
